import { calcUsageCostCents, formatCents, type SessionCvx } from '@packages/shared';
import { format } from 'date-fns';
import { CircleCheck, Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';

export const SessionSummaryContent = ({ session }: { session: SessionCvx }) => {
  return (
    <>
      {/* Session Ended Banner */}
      <div className="space-y-3 rounded-2xl border bg-card p-5 text-center">
        <CircleCheck className="mx-auto size-10 text-primary" />
        <div>
          <h2 className="text-lg font-semibold tracking-tight">Thanks for your stay</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Your session is complete. Power and water have been turned off.
          </p>
        </div>
      </div>

      {/* Card 1 — Stay Summary */}
      <div className="space-y-4 rounded-2xl border bg-card p-5">
        <h2 className="text-lg font-semibold tracking-tight">Stay summary</h2>
        <div className="grid gap-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Check in</span>
            <span>{format(new Date(session._creationTime), "h:mmaaa', 'd MMM yyyy")}</span>
          </div>
          {session.meterReadEndAt && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Check out</span>
              <span>{format(new Date(session.meterReadEndAt), "h:mmaaa', 'd MMM yyyy")}</span>
            </div>
          )}
        </div>
      </div>

      {/* Card 2 — Usage Summary */}
      <div className="space-y-4 rounded-2xl border bg-card p-5">
        <h2 className="text-lg font-semibold tracking-tight">Usage summary</h2>
        <div className="grid gap-2 text-sm">
          {(() => {
            const powerCost =
              session.electricityUnits != null && session.electricityUnitPrice != null
                ? calcUsageCostCents(session.electricityUnits, session.electricityUnitPrice)
                : 0;
            const waterCost =
              session.waterUnits != null && session.waterUnitPrice != null
                ? calcUsageCostCents(session.waterUnits, session.waterUnitPrice)
                : 0;
            return (
              <>
                <div className="flex justify-between">
                  <div>
                    <span>Power usage</span>
                    <p className="text-xs text-muted-foreground">
                      {session.electricityUnits != null
                        ? `${session.electricityUnits.toFixed(1)} kWh at ${formatCents(session.electricityUnitPrice, '$')}/kWh`
                        : '-'}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {`Meter reads: ${session.meterReadStart != null ? `${session.meterReadStart.toFixed(1)}` : '-'} to ${session.meterReadEnd != null ? `${session.meterReadEnd.toFixed(1)}` : '-'} kWh`}
                    </p>
                  </div>
                  <span className="shrink-0 font-bold">{formatCents(powerCost, '$')}</span>
                </div>
                <div className="flex justify-between">
                  <div>
                    <span>Water usage</span>
                    <p className="text-xs text-muted-foreground">
                      {session.waterUnits != null
                        ? `${session.waterUnits.toFixed(1)} units at ${formatCents(session.waterUnitPrice, '$')}/unit`
                        : '-'}
                    </p>
                  </div>
                  <span className="shrink-0 font-bold">{formatCents(waterCost, '$')}</span>
                </div>
                <Separator />
                <div className="flex justify-between">
                  <span className="font-bold">Amount charged</span>
                  <span className="font-bold">{formatCents(powerCost + waterCost, '$')}</span>
                </div>
                {(() => {
                  const totalPaid = session.payments.reduce((sum, p) => sum + p.amount, 0);
                  const allRefunds = session.payments.flatMap((p) => p.refunds);
                  const successfulRefunds = allRefunds.filter(
                    (r) => r.status === 'SUCCESS' || r.status === 'PENDING',
                  );
                  const failedRefunds = allRefunds.filter((r) => r.status === 'FAILED');
                  const totalRefunded = successfulRefunds.reduce((sum, r) => sum + r.amount, 0);
                  const netTotal = totalPaid - totalRefunded;
                  return (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Total paid</span>
                        <span>{formatCents(totalPaid, '$')}</span>
                      </div>
                      {totalRefunded > 0 && (
                        <div className="flex justify-between">
                          <div>
                            <span>Refund to card</span>
                            {successfulRefunds.some((r) => r.status === 'PENDING') ? (
                              <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                <Loader2 className="size-3 animate-spin will-change-transform" />
                                Processing... (this can take 2-3 min)
                              </p>
                            ) : (
                              <p className="text-xs text-muted-foreground">
                                Processed successfully
                              </p>
                            )}
                          </div>
                          <span className="shrink-0">{formatCents(totalRefunded, '-$')}</span>
                        </div>
                      )}
                      {failedRefunds.length > 0 && (
                        <div className="flex justify-between text-destructive">
                          <span>Refund failed</span>
                          <span>
                            {formatCents(
                              failedRefunds.reduce((sum, r) => sum + r.amount, 0),
                              '$',
                            )}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold">
                        <span>Net total</span>
                        <span>{formatCents(netTotal, '$')}</span>
                      </div>
                    </>
                  );
                })()}
              </>
            );
          })()}
        </div>
      </div>
    </>
  );
};
